import { useRef, useState } from 'react';
import { Camera, CheckCircle2, Clock, FileBadge, ImagePlus, Loader2, XCircle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import Sheet from '../../shared/ui/Sheet';
import { updateDriver } from '../../shared/services/userService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { serverTimestamp } from 'firebase/firestore';
import { storage } from '../../shared/firebase';

// Mirrors iOS SubmitDocumentsView — upload the Food Handler's Permit photo
// to Storage and stamp the drivers/{id} doc for admin review.
const STATUS = {
  approved: { label: 'Permit Approved', sub: "Your food handler's permit is verified", color: 'text-green-600', bg: 'bg-green-50', Icon: CheckCircle2 },
  pending: { label: 'Pending Review', sub: 'Our team is reviewing your permit', color: 'text-orange-500', bg: 'bg-orange-50', Icon: Clock },
  rejected: { label: 'Permit Rejected', sub: 'Please upload a valid, readable permit', color: 'text-red-500', bg: 'bg-red-50', Icon: XCircle },
  not_submitted: { label: 'Permit Required', sub: "A valid food handler's permit is required", color: 'text-gray-500', bg: 'bg-gray-100', Icon: FileBadge },
};

export default function PermitSheet({ open, onClose, driverId, driver }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [localURL, setLocalURL] = useState('');

  const permitURL = driver?.foodHandlerPermitURL || driver?.raw?.foodHandlerPermitURL || '';
  const status = driver?.foodHandlerPermitStatus || driver?.raw?.foodHandlerPermitStatus
    || (permitURL ? 'pending' : 'not_submitted');
  const s = STATUS[status] || STATUS.not_submitted;

  const upload = async (file) => {
    if (!file || !driverId) return;
    setBusy(true);
    setError('');
    try {
      const storageRef = ref(storage, `permits/drivers/${driverId}/food-handler-permit.jpg`);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      await updateDriver(driverId, {
        foodHandlerPermitURL: url,
        foodHandlerPermitStatus: 'pending',
        foodHandlerPermitSubmittedAt: serverTimestamp(),
      });
      setLocalURL(url);
    } catch (err) {
      console.error('permit upload', err);
      setError('Failed to upload permit. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLocalURL(URL.createObjectURL(file));
    upload(file);
  };

  // On Capacitor the <input capture> attr opens a file chooser, not the
  // camera — use the Camera plugin natively, keep the input as web fallback.
  const takePhoto = async () => {
    if (!Capacitor.isNativePlatform()) {
      cameraRef.current?.click();
      return;
    }
    try {
      const photo = await CapCamera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        quality: 85,
      });
      if (!photo?.webPath) return;
      const res = await fetch(photo.webPath);
      const blob = await res.blob();
      setLocalURL(URL.createObjectURL(blob));
      await upload(new File([blob], 'food-handler-permit.jpg', { type: 'image/jpeg' }));
    } catch (err) {
      if (err?.message && /cancel/i.test(err.message)) return;
      console.error('permit camera', err);
      setError('Could not open the camera. Check camera permission in Settings.');
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Food Handler's Permit">
      <div className="px-4 pb-6 space-y-4">
        <div className={`flex items-center gap-3 rounded-2xl p-4 ${s.bg}`}>
          <s.Icon size={24} className={s.color} />
          <div>
            <p className={`text-sm font-bold ${s.color}`}>{s.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.sub}</p>
          </div>
        </div>

        {localURL || permitURL ? (
          <img
            src={localURL || permitURL}
            alt="Food handler's permit"
            className="w-full max-h-72 object-contain rounded-2xl bg-gray-50 border border-gray-100"
          />
        ) : (
          <div className="h-44 rounded-2xl bg-gray-100 flex flex-col items-center justify-center gap-2 text-gray-400">
            <FileBadge size={40} />
            <p className="text-sm font-semibold">No Permit Uploaded</p>
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full py-3 rounded-2xl bg-blue-500 text-white text-sm font-bold flex items-center justify-center gap-2 active:bg-blue-600 disabled:opacity-50"
        >
          {busy ? <Loader2 size={17} className="animate-spin" /> : <ImagePlus size={17} />}
          {localURL || permitURL ? 'Replace Permit' : "Upload Food Handler's Permit"}
        </button>
        <button
          type="button"
          onClick={takePhoto}
          disabled={busy}
          className="w-full py-3 rounded-2xl bg-[#FF6B35] text-white text-sm font-bold flex items-center justify-center gap-2 active:bg-orange-600 disabled:opacity-50"
        >
          <Camera size={17} />
          Take Photo
        </button>

        <p className="text-xs text-gray-400 text-center px-2">
          Upload a clear photo of your valid Food Handler's Permit issued by the
          Ministry of Health. It will be reviewed by our team.
        </p>

        {error && <p className="text-xs text-red-500 text-center">{error}</p>}
      </div>
    </Sheet>
  );
}
